import React from 'react';
import {useState, useEffect, useCallback} from 'react';
import {
    TextField,
    Button,
    Grid,
    MenuItem,
    Typography,
    Paper,
    InputLabel,
    Select,
    FormControl,
} from '@mui/material';
import JobInput from './JobInput';

// These choices should ideally be fetched from the API or a shared config
const GENDER_CHOICES = [
    {value: 'MALE', label: 'Male'},
    {value: 'FEMALE', label: 'Female'},
    {value: 'DIVERSE', label: 'Diverse'},
    {value: 'INTER', label: 'Inter'},
    {value: 'NONBINARY', label: 'Nonbinary'},
    {value: 'GENDERFLUID', label: 'Genderfluid'},
    {value: 'NONE', label: 'None'},
];

const REGION_CHOICES = [
    {value: 'BW', label: 'Baden-Württemberg'},
    {value: 'BY', label: 'Bavaria'},
    {value: 'BE', label: 'Berlin'},
    {value: 'BB', label: 'Brandenburg'},
    {value: 'HB', label: 'Bremen'},
    {value: 'HH', label: 'Hamburg'},
    {value: 'HE', label: 'Hesse'},
    {value: 'MV', label: 'Mecklenburg-Vorpommern'},
    {value: 'NI', label: 'Lower Saxony'},
    {value: 'NW', label: 'North Rhine-Westphalia'},
    {value: 'RP', label: 'Rhineland-Palatinate'},
    {value: 'SL', label: 'Saarland'},
    {value: 'SN', label: 'Saxony'},
    {value: 'ST', label: 'Saxony-Anhalt'},
    {value: 'SH', label: 'Schleswig-Holstein'},
    {value: 'TH', label: 'Thuringia'},
];

const RELATIONSHIP_CHOICES = [
    {value: 'SINGLE', label: 'Single'},
    {value: 'COUPLE', label: 'Couple'},
    {value: 'MARRIED', label: 'Married'},
    {value: 'POLY', label: 'Poly'},
    {value: 'COLORFUL', label: 'Colorful life'},
];


const UserForm = ({
                      formData,
                      handleSubmit,
                      isRegister = false,
                      formTitle,
                  }) => {
    const getInitialState = useCallback((data) => ({
        ...data,
        // The display values for decimal fields are stored locally
        height_cm: data.height_mm ? (data.height_mm / 10).toFixed(1) : '',
        weight_kg: data.weight_g ? (data.weight_g / 1000).toFixed(3) : '',
        yearly_income: data.yearly_income_cents ? (data.yearly_income_cents / 100).toFixed(2) : '',
        job: data.job ? JSON.stringify(data.job, null, 2) : '[]',
        // Ensure passwords are not pre-filled from props
        password: '',
        password2: '',
    }), []);

    // Local state to manage the form's values during editing
    const [localData, setLocalData] = useState(() => getInitialState(formData));
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    // This effect synchronizes the local state if the parent `formData` changes (e.g., on initial data load)
    useEffect(() => {
        setLocalData(getInitialState(formData));
        setTouched({});
    }, [formData, getInitialState]);

    const convertToFloat = (inputString, maxDecimalPlaces = 2) => {
        // 1. Check for invalid characters.
        // The regex ^[0-9,.-]*$ ensures the string only contains digits, commas, or dots from start to end.
        if (!/^[0-9,.-]*$/.test(inputString)) {
            throw new Error("Invalid characters in input. Only digits, commas, and dots are allowed.");
        }

        // Handle empty or whitespace-only strings
        if (inputString.trim() === '') {
            throw new Error("Input string cannot be empty.");
        }

        // 2. Find the rightmost comma or dot to use as the decimal separator.
        const lastCommaIndex = inputString.lastIndexOf(',');
        const lastDotIndex = inputString.lastIndexOf('.');

        let decimalSeparator = null;
        let decimalSeparatorIndex = -1;
        let thousandsSeparator = null;

        if (lastCommaIndex > lastDotIndex) {
            decimalSeparator = ',';
            decimalSeparatorIndex = lastCommaIndex;
            thousandsSeparator = '.';
        } else if (lastDotIndex > lastCommaIndex) {
            decimalSeparator = '.';
            decimalSeparatorIndex = lastDotIndex;
            thousandsSeparator = ',';
        }

        // 3. Validate the number of decimal places.
        if (decimalSeparatorIndex !== -1) {
            const decimalPart = inputString.substring(decimalSeparatorIndex + 1);
            if (decimalPart.length > maxDecimalPlaces) {
                throw new Error(`Exceeded maximum allowed decimal places. Expected a maximum of ${maxDecimalPlaces}, but found ${decimalPart.length}.`);
            }
        }

        // 4. Normalize the string for parsing.
        let normalizedString = inputString;

        // Remove all thousands separators.
        if (thousandsSeparator) {
            // We need a regex to replace all occurrences.
            const thousandsRegex = new RegExp(`\\${thousandsSeparator}`, 'g');
            normalizedString = normalizedString.replace(thousandsRegex, '');
        } else if (decimalSeparatorIndex === -1) {
            // No decimal separator found, so both commas and dots are thousands separators.
            normalizedString = normalizedString.replace(/[,.]/g, '');
        }

        // Replace the decimal separator (if any) with a dot for parseFloat.
        if (decimalSeparator) {
            normalizedString = normalizedString.replace(decimalSeparator, '.');
        }

        // 5. Perform the conversion and check for NaN.
        const result = parseFloat(normalizedString);

        if (isNaN(result)) {
            throw new Error("Conversion resulted in NaN. Please check the input format (e.g., '.,' is invalid).");
        }

        // 6. Return the final, valid number.
        return result;
    }

    const validate = useCallback((data) => {
        const tempErrors = {};
        // Email validation
        if (!data.email) {
            tempErrors.email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(data.email)) {
            tempErrors.email = "Email is not valid.";
        }

        // Password validation (only for registration)
        if (isRegister) {
            if (!data.password) {
                tempErrors.password = "Password is required.";
            } else if (data.password.length < 8) {
                tempErrors.password = "Password must be at least 8 characters long.";
            }
            if (data.password !== data.password2) {
                tempErrors.password2 = "Passwords do not match.";
            }
        }

        // Year of birth validation
        const year = parseInt(data.year_of_birth, 10);
        if (data.year_of_birth && (year < 1900 || year > new Date().getFullYear())) {
            tempErrors.year_of_birth = "Please enter a valid year.";
        }

        // Number validation for decimal fields
        if (data.height_cm) {
            try {
                if (convertToFloat(data.height_cm, 2) <= 0) {
                    tempErrors.height_cm = "Height must be a positive number.";
                } else {
                    delete tempErrors.height_cm;
                }
            } catch (error) {
                tempErrors.height_cm = error.message;
            }
        }
        if (data.weight_kg) {
            try {
                if (convertToFloat(data.weight_kg, 3) <= 0) {
                    tempErrors.weight_kg = "Weight must be a positive number.";
                } else {
                    delete tempErrors.weight_kg;
                }
            } catch (error) {
                tempErrors.weight_kg = error.message;
            }
        }
        if (data.yearly_income) {
            try {
                if (convertToFloat(data.yearly_income, 2) < 0) {
                    tempErrors.yearly_income = "Income cannot be negative.";
                } else {
                    delete tempErrors.yearly_income;
                }
            } catch (error) {
                tempErrors.yearly_income = error.message;
            }
        }

        setErrors(tempErrors);
        isFormValid = Object.keys(tempErrors).length === 0;
        return isFormValid;
    }, [isRegister]);

    // Validate the form whenever the data changes
    useEffect(() => {
        validate(localData);
    }, [localData, validate]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setLocalData(prevData => ({...prevData, [name]: value}));
    };

    const handleBlur = (e) => {
        const {name} = e.target;
        setTouched(prev => ({...prev, [name]: true}));
    };

    const localSubmitHandler = (e) => {
        e.preventDefault();

        // Mark all fields as touched to show all errors on submit
        const allTouched = Object.keys(localData).reduce((acc, key) => {
            acc[key] = true;
            return acc;
        }, {});
        setTouched(allTouched);

        if (!validate(localData)) {
            console.log("Submission blocked due to validation errors.");
            return;
        }

        // Prepare data for the API: convert display values back to backend format
        const dataToSend = {...localData};

        // Convert and clean up decimal fields
        dataToSend.height_mm = localData.height_cm ? Math.round(convertToFloat(localData.height_cm, 2) * 10) : null;
        dataToSend.weight_g = localData.weight_kg ? Math.round(convertToFloat(localData.weight_kg, 3) * 1000) : null;
        dataToSend.yearly_income_cents = localData.yearly_income ? Math.round(convertToFloat(localData.yearly_income, 2) * 100) : null;

        // Parse job JSON string
        try {
            dataToSend.job = JSON.parse(localData.job);
        } catch (error) {
            dataToSend.job = null;
        }

        // Remove temporary/display fields not present on the backend model
        delete dataToSend.height_cm;
        delete dataToSend.weight_kg;
        delete dataToSend.yearly_income;
        delete dataToSend.password2;

        // Call the parent component's submit handler with the processed data
        handleSubmit(dataToSend);
    };

    let isFormValid = Object.keys(errors).length === 0;

    return (
        <Paper elevation={3} sx={{p: 4, mt: 4}}>
            <Typography variant="h4" component="h1" gutterBottom>
                {formTitle}
            </Typography>
            <form onSubmit={localSubmitHandler} noValidate>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            label="Email"
                            name="email"
                            type="email"
                            value={localData.email || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.email && !!errors.email}
                            helperText={touched.email && errors.email}
                            fullWidth
                            required
                        />
                    </Grid>
                    {isRegister && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Password"
                                    name="password"
                                    type="password"
                                    value={localData.password || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.password && !!errors.password}
                                    helperText={touched.password && errors.password}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Confirm Password"
                                    name="password2"
                                    type="password"
                                    value={localData.password2 || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.password2 && !!errors.password2}
                                    helperText={touched.password2 && errors.password2}
                                    fullWidth
                                    required
                                />
                            </Grid>
                        </>
                    )}
                    {!isRegister && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="First Name"
                                    name="first_name"
                                    value={localData.first_name || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Last Name"
                                    name="last_name"
                                    value={localData.last_name || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Year of Birth"
                                    name="year_of_birth"
                                    type="number"
                                    value={localData.year_of_birth || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.year_of_birth && !!errors.year_of_birth}
                                    helperText={touched.year_of_birth && errors.year_of_birth}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Country of Residence"
                                    name="country_of_residence"
                                    value={localData.country_of_residence || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="ZIP Code"
                                    name="zip_code"
                                    value={localData.zip_code || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth variant="outlined" style={{minWidth: "14.5em"}}>
                                    <InputLabel id="region-select-label">Region</InputLabel>
                                    <Select
                                        variant="outlined"
                                        labelId="region-select-label"
                                        id="region-select"
                                        name="region"
                                        value={localData.region || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Region"
                                    >
                                        {REGION_CHOICES.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth variant="outlined" style={{minWidth: "14.5em"}}>
                                    <InputLabel id="gender-select-label">Gender</InputLabel>
                                    <Select
                                        variant="outlined"
                                        labelId="gender-select-label"
                                        id="gender-select"
                                        name="gender"
                                        value={localData.gender || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Gender"
                                    >
                                        {GENDER_CHOICES.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Height (cm)"
                                    name="height_cm"
                                    type="text"
                                    variant="outlined"
                                    value={localData.height_cm || ''}
                                    onChange={handleChange}
                                    onKeyDown={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.height_cm && !!errors.height_cm}
                                    helperText={touched.height_cm && errors.height_cm}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Weight (kg)"
                                    name="weight_kg"
                                    type="text"
                                    variant="outlined"
                                    value={localData.weight_kg || ''}
                                    onChange={handleChange}
                                    onKeyDown={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.weight_kg && !!errors.weight_kg}
                                    helperText={touched.weight_kg && errors.weight_kg}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Yearly Income (€)"
                                    name="yearly_income"
                                    type="text"
                                    variant="outlined"
                                    value={localData.yearly_income || ''}
                                    onChange={handleChange}
                                    onKeyDown={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.yearly_income && !!errors.yearly_income}
                                    helperText={touched.yearly_income && errors.yearly_income}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth variant="outlined" style={{minWidth: "14.5em"}}>
                                    <InputLabel id="relationship-select-label">Relationship Status</InputLabel>
                                    <Select
                                        variant="outlined"
                                        labelId="relationship-select-label"
                                        id="relationship-select"
                                        name="relationship_status"
                                        value={localData.relationship_status || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Relationship Status"
                                    >
                                        {RELATIONSHIP_CHOICES.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={18}>
                                <JobInput
                                    value={localData.job || '[]'}
                                    onChange={handleChange}
                                    style={{width: "100%", maxWidth: "44.5em"}}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
                <div style={{marginTop: "2em", textAlign: "right", width: "100%", maxWidth: "46.5em"}}>
                    <Button type="submit" variant="contained" color="primary" fullWidth
                            disabled={!isFormValid}>
                        {isRegister ? 'Register' : 'Save Changes'}
                    </Button>
                </div>
            </form>
        </Paper>
    );
};

export default UserForm;
